import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronRight, Check } from "lucide-react";
import { resolveIcon } from "@/components/admin/IconPicker";
import api from "@/lib/api";
import Breadcrumbs from "@/components/layout/Breadcrumbs";
import { useSite } from "@/contexts/SiteContext";
import SectionStyles from "@/components/sections/SectionStyles";
import SectionCta from "@/components/SectionCta";

export default function Techniques() {
  const [items, setItems] = useState([]);
  const { sectionContent } = useSite();
  const c = sectionContent("techniques_page") || {};
  useEffect(() => { api.get("/public/techniques").then((r) => setItems(r.data)).catch(() => {}); }, []);

  return (
    <div data-testid="techniques-page">
      <div data-section-key="techniques_page" data-section-title="Tehnici — Antet pagină">
      <SectionStyles scopeKey="techniques_page" content={c} />
      <section className="py-12 lg:py-20" style={{ background: "var(--ct-hero-bg)" }}>
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="mb-6"><Breadcrumbs items={[{ label: "Tehnici" }]} /></div>
          <div className="text-center">
          <div className="flex items-center justify-center gap-4 mb-5">
            <span className="gold-rule" />
            <span data-el="eyebrow" data-field="eyebrow" className="eyebrow">{c.eyebrow || "Tehnici de transformare"}</span>
            <span className="gold-rule" />
          </div>
          <h1 data-el="title" data-field="title" className="font-serif text-5xl sm:text-6xl leading-[0.98]">{c.title || "Tehnicile cu care lucrez"}</h1>
          <p data-el="subtitle" data-field="intro" className="text-base sm:text-lg leading-relaxed mt-5 max-w-2xl mx-auto" style={{ color: "var(--ct-text-muted)" }}>
            {c.intro || "Combin metode validate cu intuiție profundă, ca să ajungem la rădăcina blocajului — nu doar la simptom."}
          </p>
          </div>
        </div>
      </section>
      </div>

      <section className="py-20" style={{ background: "var(--ct-bg)" }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          {items.length === 0 ? (
            <p className="text-center" style={{ color: "var(--ct-text-muted)" }}>Tehnicile se vor afișa aici după ce le adaugi din admin.</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {items.map((t, i) => {
                const Icon = resolveIcon(t.icon);
                return (
                  <Link
                    key={t.id}
                    to={`/tehnici/${t.slug}`}
                    className="card-premium p-7 flex flex-col h-full"
                    data-testid={`technique-${i}`}
                  >
                    {t.image_url && (
                      <div className="aspect-[16/10] mb-5 -mx-7 -mt-7 overflow-hidden rounded-t-2xl">
                        <img src={t.image_url} alt={t.name} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ background: "var(--ct-emerald)", border: "1.5px solid var(--ct-gold)" }}>
                      <Icon size={20} style={{ color: "var(--ct-gold)" }} />
                    </div>
                    <h3 className="font-serif text-2xl mb-2">{t.name}</h3>
                    <p className="text-sm leading-relaxed flex-1" style={{ color: "var(--ct-text-muted)" }}>{t.summary}</p>
                    <span className="mt-5 inline-flex items-center gap-2 text-sm font-medium" style={{ color: "var(--ct-link)" }}>
                      Detalii <ChevronRight size={14} />
                    </span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export function TechniqueDetail() {
  const { slug } = useParams();
  const [t, setT] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/public/techniques/${slug}`).then((r) => setT(r.data)).catch(() => setT(null)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="py-32 text-center" style={{ color: "var(--ct-text-muted)" }}>Se încarcă...</div>;
  if (!t) return (
    <div className="py-32 text-center px-5">
      <h2 className="font-serif text-3xl mb-4">Tehnică inexistentă</h2>
      <Link to="/tehnici" className="btn-secondary">Înapoi la tehnici</Link>
    </div>
  );
  const Icon = resolveIcon(t.icon);

  // JSON-LD Service schema for Google rich results
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": t.name,
    "description": t.seo_description || t.summary || t.description || "",
    "provider": {
      "@type": "Person",
      "name": "Terapeut Georgiana",
      "url": typeof window !== "undefined" ? window.location.origin : "",
    },
    "areaServed": "Romania",
    "url": typeof window !== "undefined" ? `${window.location.origin}/tehnici/${t.slug}` : `/tehnici/${t.slug}`,
    ...(t.image_url ? { "image": t.image_url } : {}),
  };

  return (
    <div data-testid="technique-detail-page">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd).replace(/</g, "\\u003c") }} />
      <section className="py-20 lg:py-28" style={{ background: "var(--ct-hero-bg)" }}>
        <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center">
          <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ background: "var(--ct-emerald)", border: "2px solid var(--ct-gold)" }}>
            <Icon size={26} style={{ color: "var(--ct-gold)" }} />
          </div>
          <span className="eyebrow">Tehnică</span>
          <h1 className="font-serif text-5xl sm:text-6xl mt-3 leading-[0.98]">{t.name}</h1>
          {t.summary && <p className="text-lg italic font-serif mt-5" style={{ color: "var(--ct-text)" }}>{t.summary}</p>}
          {t.image_url && (
            <div className="mt-10 rounded-2xl overflow-hidden" style={{ border: "1px solid var(--ct-border)" }}>
              <img src={t.image_url} alt={t.name} className="w-full max-h-96 object-cover" />
            </div>
          )}
        </div>
      </section>

      {t.description && (
        <section className="py-16" style={{ background: "var(--ct-bg)" }}>
          <article className="max-w-3xl mx-auto px-5 lg:px-8">
            <p className="text-base leading-relaxed whitespace-pre-line" style={{ color: "var(--ct-text-muted)" }}>{t.description}</p>
          </article>
        </section>
      )}

      {Array.isArray(t.benefits) && t.benefits.length > 0 && (
        <section className="py-16" style={{ background: "var(--ct-surface-2)" }}>
          <div className="max-w-4xl mx-auto px-5 lg:px-8">
            <span className="eyebrow">Beneficii</span>
            <h2 className="font-serif text-3xl sm:text-4xl mt-3 mb-8">Ce primești</h2>
            <ul className="grid md:grid-cols-2 gap-3">
              {t.benefits.map((b, i) => (
                <li key={i} className="card-premium p-5 flex items-start gap-3" data-testid={`benefit-${i}`}>
                  <Check size={18} className="mt-0.5" style={{ color: "var(--ct-gold)" }} />
                  <span className="text-sm" style={{ color: "var(--ct-text-muted)" }}>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {Array.isArray(t.steps) && t.steps.length > 0 && (
        <section className="py-16" style={{ background: "var(--ct-bg)" }}>
          <div className="max-w-4xl mx-auto px-5 lg:px-8">
            <span className="eyebrow">Cum funcționează</span>
            <h2 className="font-serif text-3xl sm:text-4xl mt-3 mb-8">Pașii procesului</h2>
            <div className="grid md:grid-cols-3 gap-5">
              {t.steps.map((s, i) => (
                <div key={i} className="card-premium p-7" data-testid={`step-${i}`}>
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ background: "var(--ct-emerald)", border: "1.5px solid var(--ct-gold)" }}>
                    <Icon size={20} style={{ color: "var(--ct-gold)" }} />
                  </div>
                  <h3 className="font-serif text-xl mb-2">{s.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: "var(--ct-text-muted)" }}>{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-20 text-center" style={{ background: "var(--ct-emerald)", color: "var(--ct-text-inverse)" }}>
        <div className="max-w-2xl mx-auto px-5">
          <h2 className="font-serif text-4xl mb-4">Ești pregătit să-ți transformi viața?</h2>
          <p className="text-base mb-7" style={{ color: "var(--ct-gold)" }}>Programează o sesiune și începem împreună.</p>
          <SectionCta
            content={t}
            prefix="cta"
            fallbackText="Începe acum"
            fallbackLink="/contact"
            className="btn-primary bg-[var(--ct-gold)] text-[#0D1B2A] border-[var(--ct-gold)]"
          />
        </div>
      </section>
    </div>
  );
}
